import { useEffect, useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { toast } from 'sonner'
import { GripVertical, Pencil, Plus, Trash2, X } from 'lucide-react'
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core'
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { Badge } from '~/components/ui/badge'
import { Button } from '~/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~/components/ui/dialog'
import { Input } from '~/components/ui/input'
import { Label } from '~/components/ui/label'
import { Textarea } from '~/components/ui/textarea'
import { cn } from '~/lib/utils'
import { useCurrentUser } from '~/lib/current-user'
import {
  createRole,
  deleteRole,
  listRoles,
  reorderRoles,
  updateRole,
} from '~/server/roles'
import { seo } from '~/utils/seo'
import type { Permission, Role } from '@rapid-response/shared'
import { PermissionChecklist } from '~/components/admin/PermissionChecklist'

export const Route = createFileRoute('/admin/roles')({
  head: () => ({ meta: seo({ title: 'Admin — Roles' }) }),
  component: AdminRolesPage,
})

interface EditState {
  mode: 'create' | 'edit'
  role: Role | null
  name: string
  description: string
  permissions: Set<Permission>
}

function blankEditState(mode: 'create' | 'edit', role: Role | null): EditState {
  return {
    mode,
    role,
    name: role?.name ?? '',
    description: role?.description ?? '',
    permissions: new Set(role?.permissions ?? []),
  }
}

// Mirrors listRolesHandler's ORDER BY (sortOrder, name). Pre-migration
// custom roles all share sortOrder = 0, so a rename can flip the
// alphabetical tie-break — without this re-sort the row would stay in
// its old slot until the next reload.
function sortByOrder(rs: readonly Role[]): Role[] {
  return [...rs].sort((a, b) => {
    if (a.sortOrder !== b.sortOrder) return a.sortOrder - b.sortOrder
    return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
  })
}

interface SortableRoleRowProps {
  role: Role
  pendingId: string | null
  reordering: boolean
  onEdit: (role: Role) => void
  onDelete: (role: Role) => void
}

function SortableRoleRow({
  role,
  pendingId,
  reordering,
  onEdit,
  onDelete,
}: Readonly<SortableRoleRowProps>) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: role.id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  }

  return (
    <tr
      ref={setNodeRef}
      style={style}
      className={cn(
        'border-t align-top',
        isDragging && 'bg-accent/40 relative z-10 shadow-sm',
      )}
    >
      <td className="w-8 px-2 py-2 align-middle">
        <button
          type="button"
          {...attributes}
          {...listeners}
          aria-label={`Drag to reorder ${role.name}`}
          className={cn(
            'flex h-7 w-6 items-center justify-center rounded text-muted-foreground hover:bg-accent hover:text-foreground',
            'cursor-grab active:cursor-grabbing touch-none',
            reordering && 'opacity-50',
          )}
          disabled={reordering}
        >
          <GripVertical className="h-3.5 w-3.5" aria-hidden="true" />
        </button>
      </td>
      <td className="px-4 py-2 font-medium">
        {role.name}
        {role.system && (
          <Badge
            variant="secondary"
            className="ml-2 text-[10px] uppercase"
          >
            system
          </Badge>
        )}
      </td>
      <td className="px-4 py-2 text-muted-foreground">
        {role.description || <span className="italic">No description.</span>}
      </td>
      <td className="px-4 py-2">
        {role.permissions.includes('admin') ? (
          <Badge variant="default">all (admin)</Badge>
        ) : (
          <span className="text-muted-foreground">
            {role.permissions.length} permission
            {role.permissions.length === 1 ? '' : 's'}
          </span>
        )}
      </td>
      <td className="px-4 py-2 text-right">
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7"
          onClick={() => onEdit(role)}
          aria-label={`Edit role ${role.name}`}
        >
          <Pencil className="h-3.5 w-3.5" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7 text-destructive hover:text-destructive"
          onClick={() => onDelete(role)}
          disabled={role.system || pendingId === role.id}
          title={role.system ? 'System roles cannot be deleted' : 'Delete role'}
          aria-label={`Delete role ${role.name}`}
        >
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </td>
    </tr>
  )
}

function AdminRolesPage() {
  const { permissions } = useCurrentUser()
  const isAdmin = permissions?.includes('admin') ?? false

  const [roles, setRoles] = useState<Role[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [editing, setEditing] = useState<EditState | null>(null)
  const [deletingRole, setDeletingRole] = useState<Role | null>(null)
  const [pendingId, setPendingId] = useState<string | null>(null)
  // Pre-compute so the delete-dialog buttons don't repeat the same
  // `pendingId === deletingRole?.id` expression three times (sonar/ts
  // both flag the optional-chain comparison there as ambiguous).
  const isDeletingPending =
    deletingRole !== null && pendingId === deletingRole.id
  const [submitting, setSubmitting] = useState(false)
  const [reordering, setReordering] = useState(false)

  // Drag activates only after 6px of movement so a click on the grip
  // (or a Pencil/Trash button inside the row) doesn't accidentally start
  // a drag. The keyboard sensor lets admins reorder via Tab + Space/Arrow.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )

  async function load() {
    try {
      const rows = await listRoles()
      setRoles(rows)
    } catch (e) {
      setError((e as Error).message)
    }
  }

  useEffect(() => {
    if (isAdmin) void load()
  }, [isAdmin])

  async function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event
    if (!over || active.id === over.id || !roles) return
    const oldIndex = roles.findIndex((r) => r.id === active.id)
    const newIndex = roles.findIndex((r) => r.id === over.id)
    if (oldIndex < 0 || newIndex < 0) return

    // Optimistic local reorder so the row settles immediately under the
    // pointer; the server response replaces state with authoritative
    // sortOrder values once it lands.
    const previous = roles
    const next = arrayMove(roles, oldIndex, newIndex)
    setRoles(next)
    setReordering(true)
    try {
      const saved = await reorderRoles({
        data: { input: { orderedIds: next.map((r) => r.id) } },
      })
      setRoles(saved)
    } catch (e) {
      setRoles(previous)
      toast.error((e as Error).message)
    } finally {
      setReordering(false)
    }
  }

  function openCreate() {
    setEditing(blankEditState('create', null))
  }
  function openEdit(role: Role) {
    setEditing(blankEditState('edit', role))
  }
  function closeEditor() {
    setEditing(null)
  }

  async function handleSave() {
    if (!editing) return
    const name = editing.name.trim()
    const description = editing.description.trim()
    if (!name) {
      toast.error('Name is required')
      return
    }
    setSubmitting(true)
    try {
      if (editing.mode === 'create') {
        const created = await createRole({
          data: {
            input: {
              name,
              description,
              permissions: Array.from(editing.permissions),
            },
          },
        })
        setRoles((prev) => (prev ? [...prev, created] : [created]))
        toast.success(`Created role "${created.name}"`)
      } else if (editing.role) {
        const updated = await updateRole({
          data: {
            id: editing.role.id,
            input: {
              name: editing.role.system ? undefined : name,
              description,
              permissions: Array.from(editing.permissions),
            },
          },
        })
        setRoles((prev) =>
          prev
            ? sortByOrder(prev.map((r) => (r.id === updated.id ? updated : r)))
            : prev,
        )
        toast.success(`Updated role "${updated.name}"`)
      }
      closeEditor()
    } catch (e) {
      toast.error((e as Error).message)
    } finally {
      setSubmitting(false)
    }
  }

  async function handleDelete() {
    if (!deletingRole) return
    setPendingId(deletingRole.id)
    try {
      await deleteRole({ data: { id: deletingRole.id } })
      setRoles((prev) => prev?.filter((r) => r.id !== deletingRole.id) ?? null)
      toast.success(`Deleted role "${deletingRole.name}"`)
      setDeletingRole(null)
    } catch (e) {
      toast.error((e as Error).message)
    } finally {
      setPendingId(null)
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-lg font-semibold">Roles</h2>
          <p className="text-sm text-muted-foreground">
            Each user is assigned one role. A role is a named set of permissions
            from the catalog below. Seeded roles (system = yes) can be edited
            but not renamed or deleted; the <strong>admin</strong> permission
            is a master switch and grants every other permission automatically.
          </p>
        </div>
        <Button onClick={openCreate}>
          <Plus className="size-3.5" /> New role
        </Button>
      </div>

      {error && (
        <div className="rounded-md border border-destructive/50 bg-destructive/10 px-4 py-2 text-sm text-destructive">
          {error}
        </div>
      )}

      {roles === null ? (
        <div className="text-muted-foreground text-sm">Loading roles...</div>
      ) : (
        <div className="rounded-md border">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-left">
              <tr>
                <th className="w-8 px-2 py-2" aria-label="Drag to reorder" />
                <th className="px-4 py-2 font-medium">Name</th>
                <th className="px-4 py-2 font-medium">Description</th>
                <th className="px-4 py-2 font-medium">Permissions</th>
                <th className="px-4 py-2 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={roles.map((r) => r.id)}
                strategy={verticalListSortingStrategy}
              >
                <tbody>
                  {roles.map((r) => (
                    <SortableRoleRow
                      key={r.id}
                      role={r}
                      pendingId={pendingId}
                      reordering={reordering}
                      onEdit={openEdit}
                      onDelete={setDeletingRole}
                    />
                  ))}
                </tbody>
              </SortableContext>
            </DndContext>
          </table>
        </div>
      )}

      <Dialog open={!!editing} onOpenChange={(open) => !open && closeEditor()}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {editing?.mode === 'create' ? 'Create role' : `Edit role`}
            </DialogTitle>
          </DialogHeader>

          {editing && (
            <div className="space-y-4">
              <div className="space-y-1">
                <Label htmlFor="roleName">Name</Label>
                <Input
                  id="roleName"
                  value={editing.name}
                  onChange={(e) =>
                    setEditing({ ...editing, name: e.target.value })
                  }
                  placeholder="e.g. Triage Lead"
                  autoFocus={editing.mode === 'create'}
                  disabled={editing.role?.system}
                />
                {editing.role?.system && (
                  <p className="text-xs text-muted-foreground">
                    System roles cannot be renamed.
                  </p>
                )}
              </div>

              <div className="space-y-1">
                <Label htmlFor="roleDescription">Description</Label>
                <Textarea
                  id="roleDescription"
                  value={editing.description}
                  onChange={(e) =>
                    setEditing({ ...editing, description: e.target.value })
                  }
                  placeholder="A short label users will see on the role picker."
                  rows={2}
                  maxLength={200}
                />
              </div>

              <div className="space-y-2">
                <Label>Permissions</Label>
                <PermissionChecklist
                  value={editing.permissions}
                  onChange={(permissions) => setEditing((prev) => prev && { ...prev, permissions })}
                />
                <p className="text-xs text-muted-foreground">
                  Tip: ticking <span className="font-mono">admin</span> alone is
                  enough — it grants every other permission automatically.
                </p>
              </div>

              <DialogFooter>
                <Button variant="ghost" onClick={closeEditor} disabled={submitting}>
                  <X className="size-3.5" />
                  Cancel
                </Button>
                <Button onClick={handleSave} disabled={submitting}>
                  {submitting ? 'Saving...' : 'Save'}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={!!deletingRole}
        onOpenChange={(open) => !open && setDeletingRole(null)}
      >
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Delete role</DialogTitle>
          </DialogHeader>
          <p className="text-sm">
            Delete <strong>{deletingRole?.name}</strong>? Roles with assigned
            users can't be deleted — reassign them first.
          </p>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeletingRole(null)}
              disabled={isDeletingPending}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={isDeletingPending}
            >
              {isDeletingPending ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
